import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Clock, Check, X, Loader2, AlertCircle, CheckCircle2,
  MapPin, User as UserIcon,
} from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import { API, fmtDayLong } from './_helpers';

export default function RosterRespondPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const initial = params.get('action');  // "accept" | "decline" from email button
  const [shift, setShift] = useState(null);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);   // "accepted" | "declined"
  const [declineReason, setDeclineReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/roster/respond/${token}`)
      .then(r => setShift(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'Link no longer valid'));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try { await axios.post(`${API}/roster/respond/${token}/accept`); setDone('accepted'); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not accept'); }
    setBusy(false);
  };
  const decline = async () => {
    setBusy(true);
    try { await axios.post(`${API}/roster/respond/${token}/decline`, { reason: declineReason }); setDone('declined'); }
    catch (e) { setErr(e?.response?.data?.detail || 'Could not decline'); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl border shadow-sm p-6">
        <div className="text-xs font-bold tracking-[0.18em] uppercase mb-2" style={{ color: '#52c3c5' }}>SP RADIOLOGY · ROSTER</div>
        {err && (
          <div className="text-center">
            <AlertCircle className="w-10 h-10 mx-auto mb-2 text-amber-500" />
            <p className="font-semibold">{err}</p>
            <Link to="/" className="mt-3 inline-block text-sm underline">Go to dashboard</Link>
          </div>
        )}
        {!err && !shift && <Loader2 className="w-6 h-6 animate-spin mx-auto" />}
        {!err && shift && !done && (
          <>
            <h2 className="text-xl font-bold mb-1" style={{ color: '#0d2644' }}>You&apos;ve been rostered</h2>
            <p className="text-sm text-muted-foreground mb-4">Hi {shift.user_name}, please confirm or decline this shift.</p>
            <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5 text-sm">
              <div className="font-semibold" style={{ color: '#0d2644' }}>{fmtDayLong(shift.date)}</div>
              <div><Clock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{shift.start} – {shift.end} ({shift.duration_hours}h)</div>
              {shift.role_label && <div><UserIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{shift.role_label}</div>}
              {shift.location && <div><MapPin className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{shift.location}</div>}
              {shift.notes && <div className="text-xs italic mt-1">{shift.notes}</div>}
            </div>
            {initial !== 'decline' && (
              <button onClick={accept} disabled={busy} data-testid="respond-accept"
                className="mt-4 w-full text-sm font-bold py-3 rounded-lg bg-green-600 text-white inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <Check className="w-5 h-5" /> Accept shift
              </button>
            )}
            {initial !== 'accept' && (
              <>
                <Textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} placeholder="Reason for declining (optional)" rows={2} className="mt-3 text-sm" />
                <button onClick={decline} disabled={busy} data-testid="respond-decline"
                  className="mt-2 w-full text-sm font-bold py-3 rounded-lg border-2 border-red-500 text-red-600 inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  <X className="w-5 h-5" /> Decline shift
                </button>
              </>
            )}
          </>
        )}
        {done === 'accepted' && (
          <div className="text-center py-6">
            <CheckCircle2 className="w-14 h-14 mx-auto mb-3 text-green-600" />
            <h2 className="text-xl font-bold">Shift accepted</h2>
            <p className="text-sm text-muted-foreground mt-1">Thanks — the practice manager has been notified.</p>
          </div>
        )}
        {done === 'declined' && (
          <div className="text-center py-6">
            <X className="w-14 h-14 mx-auto mb-3 text-red-500" />
            <h2 className="text-xl font-bold">Shift declined</h2>
            <p className="text-sm text-muted-foreground mt-1">The practice manager will arrange cover. Thanks for letting us know.</p>
          </div>
        )}
      </div>
    </div>
  );
}
